import { Component, inject, OnInit, signal, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Trip, TripLocation } from '../../models/trip.model';
import { TripService } from '../../services/trip.service';
import { AuthService } from '../../services/auth.service';
import { FrequentPlacesService } from '../../services/frequent-places.service';
import { GoogleMapsService } from '../../services/google-maps.service';
import { LoadingService } from '../../services/loading.service';
import { FrequentPlace } from '../../models/frequent-place.model';
import { isLateEntryOverrideActive } from '../../models/user-settings.model';
import { Observable, startWith, switchMap, map, of, debounceTime, tap, finalize, firstValueFrom, catchError, combineLatest, take } from 'rxjs';
import { Timestamp } from '@angular/fire/firestore';

export interface TripDialogData {
  trip?: Trip;
  date?: Date;
  initialOrigin?: TripLocation;
  initialDestination?: TripLocation;
}

@Component({
  selector: 'app-trip-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatAutocompleteModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule
  ],
  template: `
    <div class="dialog-container">
      @if (isLoading()) {
        <div class="loading-overlay">
          <mat-progress-spinner class="bid-spinner" mode="indeterminate" diameter="48"></mat-progress-spinner>
          <span>{{ loadingMessage() }}</span>
        </div>
      }
      <h2 mat-dialog-title>{{ data.trip ? 'Edit Trip' : 'New Trip' }}</h2>
      <mat-dialog-content class="trip-form">
        @if (isLocked()) {
          <div class="locked-banner">
            <mat-icon>lock</mat-icon>
            <span>This trip is locked because the monthly cutoff date has passed or it has been approved.</span>
          </div>
        }
        @if (isLateEntryActive()) {
          <div class="late-entry-banner">
            <mat-icon>lock_open</mat-icon>
            <span>Late entry unlocked by manager. This trip will require formal approval once saved.</span>
          </div>
        }
        <form [formGroup]="tripForm">
          <div class="form-row">
          <mat-form-field appearance="outline" class="flex-1">
            <mat-label>Date</mat-label>
            <input matInput [matDatepicker]="picker" formControlName="date" [matDatepickerFilter]="dateFilter">
            <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
            <mat-datepicker #picker></mat-datepicker>
          </mat-form-field>
          
          <div class="flex-1 checkbox-container">
            <mat-checkbox formControlName="isRoundTrip">Round Trip</mat-checkbox>
          </div>
        </div>

        <div class="form-row">
          <mat-form-field appearance="outline" class="flex-2">
            <mat-label>Origin Name</mat-label>
            <input type="text"
                   placeholder="Search frequent places or enter name"
                   matInput
                   formControlName="originName"
                   [matAutocomplete]="autoOrigin">
            <mat-autocomplete #autoOrigin="matAutocomplete" 
                               (optionSelected)="onOriginSelected($event)"
                               [displayWith]="displayPlace">
              @for (place of filteredOriginPlaces$ | async; track place.id) {
                <mat-option [value]="place">
                  <div class="place-option">
                    <mat-icon>{{ place.isGC ? 'business' : 'place' }}</mat-icon>
                    <div class="place-info">
                      <div class="nickname">{{ place.nickname }}</div>
                      <div class="address">{{ place.address }}</div>
                    </div>
                  </div>
                </mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>

          <mat-form-field appearance="outline" class="flex-3">
            <mat-label>Origin Address</mat-label>
            <input matInput 
                   formControlName="originAddress" 
                   placeholder="Full address"
                   [matAutocomplete]="autoOriginAddr">
            <mat-autocomplete #autoOriginAddr="matAutocomplete"
                               (optionSelected)="onAddressSelected($event, 'origin')">
              @for (prediction of filteredOriginAddresses$ | async; track prediction.place_id) {
                <mat-option [value]="prediction.description">
                  {{ prediction.description }}
                </mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>
          
          @if (!originIsSaved()) {
            <div class="save-place-toggle" matTooltip="Save as Frequent Place">
              <mat-checkbox formControlName="saveOriginAsFrequent"></mat-checkbox>
              <mat-icon>star_outline</mat-icon>
            </div>
          }
        </div>

        <div class="form-row">
          <mat-form-field appearance="outline" class="flex-2">
            <mat-label>Destination Name</mat-label>
            <input type="text"
                   placeholder="Search frequent places or enter name"
                   matInput
                   formControlName="destinationName"
                   [matAutocomplete]="autoDest">
            <mat-autocomplete #autoDest="matAutocomplete" 
                               (optionSelected)="onDestSelected($event)"
                               [displayWith]="displayPlace">
              @for (place of filteredDestPlaces$ | async; track place.id) {
                <mat-option [value]="place">
                  <div class="place-option">
                    <mat-icon>{{ place.isGC ? 'business' : 'place' }}</mat-icon>
                    <div class="place-info">
                      <div class="nickname">{{ place.nickname }}</div>
                      <div class="address">{{ place.address }}</div>
                    </div>
                  </div>
                </mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>

          <mat-form-field appearance="outline" class="flex-3">
            <mat-label>Destination Address</mat-label>
            <input matInput 
                   formControlName="destinationAddress" 
                   placeholder="Full address"
                   [matAutocomplete]="autoDestAddr">
            <mat-autocomplete #autoDestAddr="matAutocomplete"
                               (optionSelected)="onAddressSelected($event, 'destination')">
              @for (prediction of filteredDestAddresses$ | async; track prediction.place_id) {
                <mat-option [value]="prediction.description">
                  {{ prediction.description }}
                </mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>

          @if (!destIsSaved()) {
            <div class="save-place-toggle" matTooltip="Save as Frequent Place">
              <mat-checkbox formControlName="saveDestAsFrequent"></mat-checkbox>
              <mat-icon>star_outline</mat-icon>
            </div>
          }
        </div>

        <div class="form-row miles-row">
          <mat-form-field appearance="outline" class="flex-1">
            <mat-label>Calculated Miles</mat-label>
            <input matInput type="number" formControlName="calculatedMiles" readonly>
            <mat-hint>From Google Maps</mat-hint>
          </mat-form-field>

          <mat-form-field appearance="outline" class="flex-1">
            <mat-label>Claimed Miles</mat-label>
            <input matInput type="number" formControlName="claimedMiles">
            @if (isCalculatingDistance()) {
              <mat-icon matSuffix>
                <div class="spinner-container">
                  <mat-progress-spinner class="bid-spinner" mode="indeterminate" diameter="18"></mat-progress-spinner>
                </div>
              </mat-icon>
            }
          </mat-form-field>
        </div>

        @if (isManualOverride()) {
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Override Reason</mat-label>
            <textarea matInput formControlName="overrideReason" placeholder="Explain why miles were adjusted"></textarea>
            @if (tripForm.get('overrideReason')?.hasError('required')) {
              <mat-error>Reason is required for manual overrides</mat-error>
            }
          </mat-form-field>
        }

        @if (data.trip) {
          <div class="status-info">
            <span class="label">Status:</span>
            <span class="value" [class]="data.trip.status.toLowerCase()">{{ data.trip.status }}</span>
          </div>
        }
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="cancel()">Cancel</button>
      @if (data.trip && !isLocked()) {
        <button mat-button color="warn" (click)="delete()">Delete</button>
      }
      <button mat-raised-button color="primary" 
              [disabled]="tripForm.invalid || isCalculatingDistance() || isLocked()" 
              (click)="save()">
        {{ data.trip ? 'Update' : 'Save' }}
      </button>
    </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .dialog-container {
      position: relative;
      display: flex;
      flex-direction: column;
      background-color: var(--app-trips-dialog-bg);
      border-radius: 4px; /* Default Material dialog border-radius */
      overflow: hidden;
    }

    .trip-form {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      min-width: 600px;
      padding: 20px 24px !important;
      color: var(--app-trips-text);
    }

    .loading-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: var(--app-loading-overlay-bg);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 1rem;
      z-index: 10;
      color: var(--app-trips-text);
      font-weight: 500;
      border-radius: inherit;
    }

    .full-width { width: 100%; }
    .form-row { display: flex; gap: 1rem; align-items: center; }
    .flex-1 { flex: 1; }
    .flex-2 { flex: 2; }
    .flex-3 { flex: 3; }
    
    .checkbox-container {
      display: flex;
      align-items: center;
      height: 56px;
    }

    .place-option {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 4px 0;

      mat-icon { color: var(--app-trips-text); opacity: 0.6; }
      
      .nickname { font-weight: 500; line-height: 1.2; color: var(--app-trips-text); }
      .address { font-size: 0.75rem; color: var(--app-trips-text); opacity: 0.6; line-height: 1.2; }
    }

    .miles-row {
      background-color: var(--app-trips-miles-row-bg);
      padding: 12px;
      border-radius: 4px;
      margin: 8px 0;
      border: 1px solid var(--app-trips-border);
    }

    /* Differentiate form fields from dialog background */
    ::ng-deep .trip-form {
      .mat-mdc-text-field-wrapper {
        background-color: var(--app-trips-form-field-bg) !important;
      }
      .mat-mdc-form-field-focus-overlay {
        background-color: var(--app-trips-form-field-bg) !important;
      }
    }

    .status-info {
      display: flex;
      gap: 8px;
      font-size: 0.85rem;
      margin-top: 8px;
      
      .label { color: var(--app-trips-text); opacity: 0.7; font-weight: 500; }
      .value { 
        text-transform: uppercase; font-weight: bold; 
        &.logged { color: var(--app-trips-text); opacity: 0.7; }
        &.submitted { color: #f57c00; }
        &.approved { color: #388e3c; }
      }
    }

    .spinner-container {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
    }

    .save-place-toggle {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 56px;
      margin-top: -16px; /* Align with form fields */
      opacity: 0.6;
      transition: opacity 0.2s;
      
      &:hover { opacity: 1; }
      
      mat-icon { font-size: 20px; width: 20px; height: 20px; margin-left: -4px; color: #fbc02d; }
    }

    .locked-banner {
      display: flex;
      align-items: center;
      gap: 8px;
      background-color: #fff3cd;
      color: #856404;
      padding: 10px 14px;
      border-radius: 8px;
      margin-bottom: 16px;
      font-size: 0.9rem;
      border: 1px solid #ffeeba;
    }
    :host-context(.dark-theme) .locked-banner {
      background-color: rgba(255, 193, 7, 0.15);
      color: #ffc107;
      border-color: rgba(255, 193, 7, 0.3);
    }

    .late-entry-banner {
      display: flex;
      align-items: center;
      gap: 8px;
      background-color: #e8f5e9;
      color: #2e7d32;
      padding: 10px 14px;
      border-radius: 8px;
      margin-bottom: 16px;
      font-size: 0.9rem;
      border: 1px solid #c8e6c9;
      font-weight: 500;

      mat-icon {
        color: #2e7d32;
        font-size: 20px;
        width: 20px;
        height: 20px;
      }
    }
    :host-context(.dark-theme) .late-entry-banner {
      background-color: rgba(76, 175, 80, 0.15);
      color: #81c784;
      border-color: rgba(76, 175, 80, 0.3);
      mat-icon { color: #81c784; }
    }
  `]
})
export class TripDialogComponent implements OnInit {
  protected readonly data = inject<TripDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<TripDialogComponent>);
  private readonly fb = inject(FormBuilder);
  private readonly tripService = inject(TripService);
  private readonly authService = inject(AuthService);
  private readonly placesService = inject(FrequentPlacesService);
  private readonly googleMapsService = inject(GoogleMapsService);
  private readonly loadingService = inject(LoadingService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);

  tripForm: FormGroup;
  filteredOriginPlaces$!: Observable<FrequentPlace[]>;
  filteredDestPlaces$!: Observable<FrequentPlace[]>;
  filteredOriginAddresses$!: Observable<any[]>;
  filteredDestAddresses$!: Observable<any[]>;
  
  private currentUser: any = null;
  cutoffDay = signal(10);
  isCalculatingDistance = signal(false);
  isLocked = signal(false);
  isLateEntryActive = signal(false);
  originIsSaved = signal(false);
  destIsSaved = signal(false);

  // Loading state for the dialog itself
  isLoading = this.loadingService.isLoading;
  loadingMessage = this.loadingService.loadingMessage;

  dateFilter = (d: Date | null): boolean => {
    if (!d) return false;
    if (!this.currentUser) return true;
    const isPrivileged = this.currentUser.role === 'admin' || this.currentUser.role === 'manager' || this.currentUser.role === 'accounting';
    if (isPrivileged) return true;

    // Allow existing trip's date if viewing
    if (this.data.trip) {
      const tripDate = this.data.trip.date.toDate();
      if (d.getFullYear() === tripDate.getFullYear() && d.getMonth() === tripDate.getMonth() && d.getDate() === tripDate.getDate()) {
        return true;
      }
    }

    // Check if month has an active late entry override
    const monthKey = this.tripService.getMonthKey(d);
    const override = this.currentUser?.lateEntryOverrides?.[monthKey];
    if (override && isLateEntryOverrideActive(override)) {
      return true;
    }

    const now = new Date();
    const currentDay = now.getDate();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const cutoff = this.cutoffDay();

    const dYear = d.getFullYear();
    const dMonth = d.getMonth();

    if (dYear < currentYear) {
      if (currentMonth === 0 && dMonth === 11 && dYear === currentYear - 1) {
        return currentDay <= cutoff;
      }
      return false;
    }

    if (dMonth < currentMonth) {
      if (dMonth === currentMonth - 1) {
        return currentDay <= cutoff;
      }
      return false;
    }

    return true;
  };

  constructor() {
    const trip = this.data.trip;
    this.tripForm = this.fb.group({
      date: [trip ? trip.date.toDate() : (this.data.date || new Date()), Validators.required],
      originName: [trip?.origin?.name || this.data.initialOrigin?.name || ''],
      originAddress: [trip?.origin?.address || this.data.initialOrigin?.address || '', Validators.required],
      originPlaceId: [trip?.origin?.placeId || this.data.initialOrigin?.placeId || ''],
      saveOriginAsFrequent: [false],
      destinationName: [trip?.destination?.name || this.data.initialDestination?.name || ''],
      destinationAddress: [trip?.destination?.address || this.data.initialDestination?.address || '', Validators.required],
      destinationPlaceId: [trip?.destination?.placeId || this.data.initialDestination?.placeId || ''],
      saveDestAsFrequent: [false],
      isRoundTrip: [trip ? trip.isRoundTrip : false],
      calculatedMiles: [trip ? trip.calculatedMiles : 0],
      claimedMiles: [trip ? trip.claimedMiles : 0, [Validators.required, Validators.min(0)]],
      overrideReason: [trip ? trip.overrideReason : '']
    });

    // Handle override reason validation
    this.tripForm.get('claimedMiles')?.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.updateOverrideValidation());

    this.tripForm.get('calculatedMiles')?.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.updateOverrideValidation());
  }

  async checkLockStatus(date: Date | null) {
    if (!this.currentUser) return;
    const isApproved = this.data.trip?.status === 'Approved';
    const isPrivileged = this.currentUser.role === 'admin' || this.currentUser.role === 'manager' || this.currentUser.role === 'accounting';
    const dateLocked = date ? await this.tripService.isLocked(date, this.currentUser) : false;
    const locked = (isApproved && !isPrivileged) || dateLocked;
    this.isLocked.set(locked);

    const isPastCutoff = date ? await this.tripService.isMonthPastCutoff(date) : false;
    this.isLateEntryActive.set(!locked && isPastCutoff && !isPrivileged);

    if (locked) {
      if (isApproved && !isPrivileged) {
        this.tripForm.disable({ emitEvent: false });
      }
    } else {
      if (this.tripForm.disabled) {
        this.tripForm.enable({ emitEvent: false });
      }
    }
  }

  async ngOnInit() {
    const user = await firstValueFrom(this.authService.userData$);
    if (!user) {
      this.dialogRef.close();
      return;
    }
    this.currentUser = user;

    try {
      const cutoff = await this.tripService.getCutoffDay();
      this.cutoffDay.set(cutoff);
    } catch {
      this.cutoffDay.set(10);
    }

    const initialDate: Date = this.tripForm.get('date')?.value;
    await this.checkLockStatus(initialDate);

    // Re-check lock status and prepopulate origin whenever date changes
    this.tripForm.get('date')?.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(async (date) => {
      await this.checkLockStatus(date);
      if (!this.data.trip) {
        this.prePopulateOrigin(user);
      }
    });

    if (!this.data.trip) {
      this.prePopulateOrigin(user);
    }

    // Check initial saved status
    this.updateSavedStatus(user.uid);

    // Place Name Autocomplete
    this.filteredOriginPlaces$ = this.tripForm.get('originName')!.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      switchMap(value => {
        const term = typeof value === 'string' ? value : value?.nickname || '';
        return this.placesService.searchPlaces(user.uid, term, user.sales.defaultBranch);
      }),
      tap(() => this.updateSavedStatus(user.uid))
    );

    this.filteredDestPlaces$ = this.tripForm.get('destinationName')!.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      switchMap(value => {
        const term = typeof value === 'string' ? value : value?.nickname || '';
        return this.placesService.searchPlaces(user.uid, term, user.sales.defaultBranch);
      }),
      tap(() => this.updateSavedStatus(user.uid))
    );

    // Google Maps Address Autocomplete
    this.filteredOriginAddresses$ = this.tripForm.get('originAddress')!.valueChanges.pipe(
      debounceTime(300),
      switchMap(value => {
        if (!value || typeof value !== 'string' || value.length < 3) return of([]);
        return this.googleMapsService.fetchPredictions(value).pipe(
          catchError(() => of([]))
        );
      })
    );

    this.filteredDestAddresses$ = this.tripForm.get('destinationAddress')!.valueChanges.pipe(
      debounceTime(300),
      switchMap(value => {
        if (!value || typeof value !== 'string' || value.length < 3) return of([]);
        return this.googleMapsService.fetchPredictions(value).pipe(
          catchError(() => of([]))
        );
      })
    );

    // Watch origin/destination addresses for distance calculation
    combineLatest([
      this.tripForm.get('originAddress')!.valueChanges.pipe(startWith(this.tripForm.get('originAddress')?.value), debounceTime(1000)),
      this.tripForm.get('destinationAddress')!.valueChanges.pipe(startWith(this.tripForm.get('destinationAddress')?.value), debounceTime(1000)),
      this.tripForm.get('isRoundTrip')!.valueChanges.pipe(startWith(this.tripForm.get('isRoundTrip')?.value))
    ]).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(([origin, dest, isRoundTrip]: [any, any, any]) => {
      if (origin && dest && (typeof origin === 'string') && (typeof dest === 'string') && origin.length > 5 && dest.length > 5) {
        this.calculateDistance(origin, dest, !!isRoundTrip);
      }
    });
  }

  private async updateSavedStatus(userId: string) {
    const originAddr = this.tripForm.get('originAddress')?.value;
    const destAddr = this.tripForm.get('destinationAddress')?.value;
    
    if (!originAddr && !destAddr) return;

    const places = await firstValueFrom(this.placesService.getUserFrequentPlaces(userId));
    const warehouses = await firstValueFrom(this.placesService.getWarehouses());
    const allKnown = [...places, ...warehouses];

    if (originAddr) {
      this.originIsSaved.set(allKnown.some(p => p.address === originAddr || p.nickname === this.tripForm.get('originName')?.value));
    }
    if (destAddr) {
      this.destIsSaved.set(allKnown.some(p => p.address === destAddr || p.nickname === this.tripForm.get('destinationName')?.value));
    }
  }

  private async prePopulateOrigin(user: any) {
    // Only pre-populate if origin address is empty
    if (this.tripForm.get('originAddress')?.value) {
      return;
    }

    this.loadingService.show('Pre-populating trip data...');
    try {
      // 1. Try to get the previous trip on the same date
      try {
        const date = this.tripForm.get('date')?.value;
        if (date) {
          const prevTrip = await firstValueFrom(this.tripService.getPreviousTrip(user.uid, date));
          if (prevTrip) {
            // If the previous trip was a round trip, the current trip should start at the same origin.
            // Otherwise, it starts at the previous destination.
            const sourceLocation = prevTrip.isRoundTrip ? prevTrip.origin : prevTrip.destination;
            this.tripForm.patchValue({
              originName: sourceLocation.name || '',
              originAddress: sourceLocation.address,
              originPlaceId: sourceLocation.placeId || ''
            });
            this.updateSavedStatus(user.uid);
            return;
          }
        }
      } catch (prevTripErr) {
        console.warn('Could not fetch previous trip for origin pre-population:', prevTripErr);
      }

      // 2. Fallback: Default to the explicitly set default place, or the default branch
      const defaultPlaceId = user.sales.defaultPlaceId;
      const defaultBranch = user.sales.defaultBranch;
      
      if (defaultPlaceId) {
        // Try to find in user places first
        const userPlaces = await firstValueFrom(this.placesService.getUserFrequentPlaces(user.uid));
        const foundUserPlace = userPlaces.find(p => p.id === defaultPlaceId);
        
        if (foundUserPlace) {
          this.tripForm.patchValue({
            originName: foundUserPlace.nickname,
            originAddress: foundUserPlace.address,
            originPlaceId: foundUserPlace.placeId || ''
          });
          this.updateSavedStatus(user.uid);
          return;
        }

        // Try to find as a warehouse
        const warehouse = await firstValueFrom(this.placesService.getDefaultOffice(defaultPlaceId));
        if (warehouse) {
          this.tripForm.patchValue({
            originName: warehouse.nickname,
            originAddress: warehouse.address,
            originPlaceId: warehouse.placeId || ''
          });
          this.updateSavedStatus(user.uid);
          return;
        }
      }

      // 3. Last fallback: Default branch warehouse
      if (defaultBranch) {
        const warehouse = await firstValueFrom(this.tripService.getWarehouse(defaultBranch));
        if (warehouse) {
          this.tripForm.patchValue({
            originName: warehouse.name,
            originAddress: warehouse.address
          });
          this.updateSavedStatus(user.uid);
        }
      }
    } catch (error) {
      console.error('Error pre-populating origin:', error);
    } finally {
      this.loadingService.hide();
    }
  }

  onAddressSelected(event: any, type: 'origin' | 'destination') {
    const selectedAddress = event.option.value;
    const predictions$ = type === 'origin' ? this.filteredOriginAddresses$ : this.filteredDestAddresses$;
    
    predictions$.pipe(take(1)).subscribe(async predictions => {
      const prediction = predictions.find(p => p.description === selectedAddress);
      if (prediction) {
        if (type === 'origin') {
          this.tripForm.patchValue({ originPlaceId: prediction.place_id });
        } else {
          this.tripForm.patchValue({ destinationPlaceId: prediction.place_id });
        }
        
        const user = await firstValueFrom(this.authService.userData$);
        if (user) this.updateSavedStatus(user.uid);
      }
    });
  }

  displayPlace(place: FrequentPlace | string): string {
    if (!place) return '';
    if (typeof place === 'string') return place;
    return place.nickname || place.address;
  }

  onOriginSelected(event: any) {
    const place = event.option.value as FrequentPlace;
    this.tripForm.patchValue({
      originName: place.nickname,
      originAddress: place.address,
      originPlaceId: place.placeId || ''
    });
    this.originIsSaved.set(true);
  }

  onDestSelected(event: any) {
    const place = event.option.value as FrequentPlace;
    this.tripForm.patchValue({
      destinationName: place.nickname,
      destinationAddress: place.address,
      destinationPlaceId: place.placeId || ''
    });
    this.destIsSaved.set(true);
  }

  isManualOverride(): boolean {
    const calc = this.tripForm.get('calculatedMiles')?.value || 0;
    const claimed = this.tripForm.get('claimedMiles')?.value || 0;
    return Math.abs(calc - claimed) > 0.1;
  }

  private updateOverrideValidation() {
    const reasonCtrl = this.tripForm.get('overrideReason');
    if (this.isManualOverride()) {
      reasonCtrl?.setValidators([Validators.required]);
    } else {
      reasonCtrl?.clearValidators();
    }
    reasonCtrl?.updateValueAndValidity();
  }

  private calculateDistance(origin: string, dest: string, isRoundTrip: boolean) {
    this.isCalculatingDistance.set(true);
    this.tripService.calculateDistance(origin, dest).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.isCalculatingDistance.set(false)),
      catchError(err => {
        console.error('Distance calculation error:', err);
        return of(0);
      })
    ).subscribe(miles => {
      if (miles === 0) return;

      let finalMiles = miles;
      if (isRoundTrip) {
        finalMiles *= 2;
      }
      // Round to 1 decimal
      finalMiles = Math.round(finalMiles * 10) / 10;
      
      this.tripForm.patchValue({
        calculatedMiles: finalMiles,
        claimedMiles: finalMiles
      }, { emitEvent: false });
    });
  }

  async save() {
    if (this.isLocked()) {
      this.snackBar.open('Cannot save trip: Entries for this month are locked past the cutoff date.', 'Close', { duration: 4000 });
      return;
    }
    if (this.tripForm.invalid) return;

    const formValue = this.tripForm.value;
    const user = await firstValueFrom(this.authService.userData$);
    if (!user) return;

    // Save as Frequent Places if requested
    if (formValue.saveOriginAsFrequent) {
      await this.placesService.saveFrequentPlace(user.uid, {
        nickname: typeof formValue.originName === 'string' ? formValue.originName : formValue.originName.nickname,
        address: formValue.originAddress,
        placeId: formValue.originPlaceId
      });
    }

    if (formValue.saveDestAsFrequent) {
      await this.placesService.saveFrequentPlace(user.uid, {
        nickname: typeof formValue.destinationName === 'string' ? formValue.destinationName : formValue.destinationName.nickname,
        address: formValue.destinationAddress,
        placeId: formValue.destinationPlaceId
      });
    }
    
    const trip: Partial<Trip> = {
      ...this.data.trip,
      date: Timestamp.fromDate(new Date(Date.UTC(
        formValue.date.getFullYear(),
        formValue.date.getMonth(),
        formValue.date.getDate()
      ))),
      origin: {
        name: typeof formValue.originName === 'string' ? formValue.originName : formValue.originName.nickname,
        address: formValue.originAddress,
        placeId: formValue.originPlaceId
      },
      destination: {
        name: typeof formValue.destinationName === 'string' ? formValue.destinationName : formValue.destinationName.nickname,
        address: formValue.destinationAddress,
        placeId: formValue.destinationPlaceId
      },
      isRoundTrip: formValue.isRoundTrip,
      calculatedMiles: formValue.calculatedMiles,
      claimedMiles: formValue.claimedMiles,
      isManualOverride: this.isManualOverride(),
      overrideReason: formValue.overrideReason,
      status: this.data.trip?.status || 'Logged'
    };

    try {
      await this.tripService.saveTrip(trip);
      this.dialogRef.close(true);
    } catch (error: any) {
      console.error('Error saving trip:', error);
      this.snackBar.open(`Could not save trip: ${error?.message || 'Permission denied or network error'}`, 'Close', {
        duration: 5000
      });
    }
  }

  async delete() {
    if (!this.data.trip?.id || this.isLocked()) return;
    if (confirm('Are you sure you want to delete this trip?')) {
      try {
        await this.tripService.deleteTrip(this.data.trip.id);
        this.dialogRef.close(true);
      } catch (error: any) {
        console.error('Error deleting trip:', error);
        this.snackBar.open(`Could not delete trip: ${error?.message || 'Permission denied or network error'}`, 'Close', {
          duration: 5000
        });
      }
    }
  }

  cancel() {
    this.dialogRef.close();
  }
}
